import { get, omit } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  type AtlasGqlInspection,
  type AtlasGqlInspectionDelivery,
  type AtlasGqlInspectionTask,
  type AtlasGqlOrganization,
  AtlasGqlInspectionDeliveryStatus,
  AtlasGqlWorkContainerTypeOption,
  useGetInspectionTaskDeliveriesQuery,
  useInitiateInspectionDeliveryMutation,
} from 'types/atlas-graphql';

import { None } from 'components/data/helpers';
import * as inspectionData from 'components/data/inspections';
import { getMostRecentDeliveryForTargetCustomer } from 'components/data/tasks/tasks';
import { type TDisplayColumnData } from 'components/data/types';
import { StyledTableV5 } from 'components/DataTable/DataTable.style';
import { Button, Modal, useMessage } from 'components/ui';
import { useAccountContext } from 'utils/account/AccountContext';
import { StatusTag } from 'utils/color/helpers';
import { formatDate } from 'utils/format';
import { IDD_COMMENTS } from 'utils/release-toggles';

import {
  ButtonContainer,
  CheckboxHelpText,
  IncludeCommentsCheckbox,
  TableWrapper,
} from './InspectionActions.style';

const POLL_INTERVAL = 2000;

type DeliverInspectionsProps = {
  open: boolean;
  onClose: () => void;
  inspectionTasks: AtlasGqlInspectionTask[];
  forOrg?: AtlasGqlOrganization;
};

export const DeliverInspections: React.FC<DeliverInspectionsProps> = ({
  open,
  onClose,
  inspectionTasks,
  forOrg,
}) => {
  const message = useMessage();
  const { t } = useTranslation();
  const { hasReleaseToggle } = useAccountContext();
  const hasIddComments = hasReleaseToggle(IDD_COMMENTS);

  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [inspectionDeliveryLoading, setInspectionDeliveryLoading] = useState<boolean>(false);
  const [tasksPolling, setTasksPolling] = useState<boolean>(false);
  const [includeComments, setIncludeComments] = useState(false);
  const hasChangedIncludeComments = useRef(false);

  const { inspectionTaskIds, inspections } = useMemo(() => {
    return {
      inspectionTaskIds: inspectionTasks.map(task => task.id) as string[],
      inspections: inspectionTasks.map(task => {
        return {
          ...task.inspection,
          task,
          asset: task.asset,
        };
      }),
    };
  }, [inspectionTasks]);

  const previousCommentDeliveries = useMemo(
    () =>
      inspectionTasks.map(inspectionTask => {
        const targetOrganizationId = inspectionTask.forOrg?.id;
        return (
          !!targetOrganizationId &&
          (inspectionTask.inspection?.deliveries?.some(
            delivery =>
              delivery.targetOrganizationId === targetOrganizationId &&
              delivery.deliveryOptions?.includeComments === true
          ) ??
            false)
        );
      }),
    [inspectionTasks]
  );
  const everyInspectionPreviouslyIncludedComments =
    previousCommentDeliveries.length > 0 && previousCommentDeliveries.every(Boolean);
  const anyInspectionPreviouslyIncludedComments = previousCommentDeliveries.some(Boolean);

  useEffect(() => {
    if (!open) {
      hasChangedIncludeComments.current = false;
      return;
    }
    if (hasIddComments && !hasChangedIncludeComments.current) {
      setIncludeComments(everyInspectionPreviouslyIncludedComments);
    }
  }, [everyInspectionPreviouslyIncludedComments, hasIddComments, open]);

  const columns = useMemo(() => {
    return [
      {
        title: t('table.task', 'Task'),
        key: 'taskNumber',
        dataIndex: ['task'],
        get: (record: any) => get(record, 'task'),
        render: (task: AtlasGqlInspectionTask, _record: AtlasGqlInspection) => {
          const { id, number } = task ?? {};
          return [id, number].every(Boolean) ? <Link to={`/tasks/${id}`}>{number}</Link> : <None />;
        },
      },
      {
        ...inspectionData.date,
        title: t('workOrder.inspectionDate', 'Inspection Date'),
      },
      {
        title: t('table.deliveryStatus', 'Delivery Status'),
        key: 'deliveryStatus',
        dataIndex: ['deliveries'],
        get: (record: AtlasGqlInspection) => get(record, ['deliveries']),
        render: (deliveries: AtlasGqlInspectionDelivery[], _record: AtlasGqlInspection) => {
          const delivery = getMostRecentDeliveryForTargetCustomer(
            deliveries ?? [],
            forOrg?.id ?? ''
          );
          const status = delivery?.status;
          return status ? (
            <StatusTag status={status} />
          ) : (
            t('workOrder.notDelivered', 'Not Delivered')
          );
        },
      },
      {
        title: t('workOrder.deliveryDate', 'Delivery Date'),
        key: 'deliveryDate',
        dataIndex: ['deliveries'],
        get: (record: AtlasGqlInspectionTask) => get(record, ['deliveries']),
        render: (deliveries: AtlasGqlInspectionDelivery[], _record: AtlasGqlInspection) => {
          const delivery = getMostRecentDeliveryForTargetCustomer(
            deliveries ?? [],
            forOrg?.id ?? ''
          );
          const created = delivery?.created;
          return created ? formatDate(created) : t('common.notApplicable', 'N/A');
        },
      },
      inspectionData.vendor,
      inspectionData.type,
      inspectionData.site,
      {
        ...inspectionData.assetWithoutFilters,
        title: t('table.component', 'Component'),
      },
      inspectionData.createdDate,
    ].map(column => omit(column, ['filterDropdown', 'sorter'])) as TDisplayColumnData[];
  }, [forOrg, t]);

  const {
    data: deliveriesData,
    startPolling: startPollingTasks,
    stopPolling: stopPollingTasks,
  } = useGetInspectionTaskDeliveriesQuery({
    variables: {
      filterBy: { key: 'id', values: inspectionTaskIds },
      limit: inspectionTaskIds.length,
    },
    skip: !open || !tasksPolling || inspectionTaskIds.length === 0,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!deliveriesData) {
      return;
    }
    const tasksWithCompletedDeliveries = (deliveriesData?.tasks?.items ?? []).filter(_task => {
      const task = _task as AtlasGqlInspectionTask;
      if (task?.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask) {
        const delivery = getMostRecentDeliveryForTargetCustomer(
          task.inspection?.deliveries ?? [],
          task.forOrg?.id ?? ''
        );
        const status = delivery?.status;

        return (
          status &&
          [
            AtlasGqlInspectionDeliveryStatus.Completed,
            AtlasGqlInspectionDeliveryStatus.Failed,
          ].includes(status)
        );
      }

      return false;
    });

    if (tasksWithCompletedDeliveries.length === inspectionTaskIds.length) {
      message.success(
        t(
          'workOrder.allInspectionDeliveriesCompleted',
          'All Inspection Deliveries have been completed.'
        )
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveriesData, inspectionTaskIds]);

  const [initiateInspectionDelivery] = useInitiateInspectionDeliveryMutation();

  const handleDeliver = async () => {
    setConfirmModalOpen(false);

    setInspectionDeliveryLoading(true);
    for (const inspectionTask of inspectionTasks) {
      const inspectionId = inspectionTask.inspection?.id;
      const targetOrgId = inspectionTask.forOrg?.id;

      if (inspectionId && targetOrgId) {
        await initiateInspectionDelivery({
          variables: {
            input: {
              id: inspectionId,
              targetOrganizationId: targetOrgId,
              ...(hasIddComments && { includeComments }),
            },
          },
          update(cache, { data }) {
            const delivery = data?.initiateInspectionDelivery?.inspectionDelivery;
            if (delivery) {
              cache.modify({
                id: cache.identify(inspectionTask.inspection as AtlasGqlInspection),
                fields: {
                  deliveries(currentDeliveries = []) {
                    return [...currentDeliveries, delivery];
                  },
                },
              });
            }
          },
        });
      }
    }
    setInspectionDeliveryLoading(false);

    startPollingTasks(POLL_INTERVAL);
    setTasksPolling(true);
  };

  const handleClose = () => {
    stopPollingTasks();
    setTasksPolling(false);
    setIncludeComments(false);
    hasChangedIncludeComments.current = false;

    onClose();
  };

  return (
    <>
      {inspectionTasks.length > 0 && forOrg ? (
        <>
          <Modal
            data-testid="wo-inspection-delivery-modal"
            title={t('workOrder.deliverInspections', 'Deliver Inspections')}
            open={open}
            onCancel={handleClose}
            footer={
              <ButtonContainer>
                <Button onClick={handleClose}>{t('common.cancel', 'Cancel')}</Button>
                <Button
                  type="primary"
                  onClick={() => setConfirmModalOpen(true)}
                  loading={inspectionDeliveryLoading}
                >
                  {t('workOrder.deliverInspections', 'Deliver Inspections')}
                </Button>
              </ButtonContainer>
            }
            width={1000}
          >
            <span>
              {t(
                'workOrder.followingInspectionsCanBeDelivered',
                'The following Inspections can be delivered to'
              )}{' '}
              <b>{forOrg.name}</b>:
            </span>
            <TableWrapper style={{ overflowY: 'auto' }}>
              <StyledTableV5
                data-testid="wo-inspection-delivery-tasks-table"
                loading={tasksPolling}
                columns={columns}
                dataSource={inspections}
                rowKey="id"
                pagination={false}
              />
            </TableWrapper>
            {hasIddComments && (
              <>
                <IncludeCommentsCheckbox
                  checked={includeComments}
                  onChange={event => {
                    hasChangedIncludeComments.current = true;
                    setIncludeComments(event.target.checked);
                  }}
                >
                  {t('workOrder.includeInspectionCommentsBulk', 'Include Comments')}
                </IncludeCommentsCheckbox>
                <CheckboxHelpText>
                  {t(
                    'workOrder.includeInspectionCommentsBulkDescription',
                    'All comments on these Inspections’ Damages and Observations will be delivered to the selected Customer.'
                  )}
                </CheckboxHelpText>
                {anyInspectionPreviouslyIncludedComments && (
                  <CheckboxHelpText>
                    {t(
                      'workOrder.inspectionCommentsPreviouslyDeliveredBulk',
                      'Note: Comments were included in a previous delivery for at least one selected Inspection.'
                    )}
                  </CheckboxHelpText>
                )}
              </>
            )}
          </Modal>
          <Modal
            data-testid="wo-inspection-delivery-modal"
            title={t('workOrder.confirmDelivery', 'Confirm Delivery')}
            open={confirmModalOpen}
            okText={t('common.confirm', 'Confirm')}
            onOk={handleDeliver}
            onCancel={() => setConfirmModalOpen(false)}
            cancelText={t('common.cancel', 'Cancel')}
            loading={false}
          >
            <p>
              <Trans
                i18nKey="workOrder.areYouSureDeliverTheseInspections"
                t={t}
                count={inspectionTasks.length}
                components={{ bold: <b /> }}
                values={{ orgName: forOrg.name }}
                defaults="Are you sure that you want to deliver these {{count}} Inspections to <bold>{{orgName}}</bold>?"
              />
            </p>
            {includeComments && (
              <p>
                {t(
                  'workOrder.includeInspectionCommentsConfirmBulk',
                  'Comments on these Inspections’ Damages and Observations will be included.'
                )}
              </p>
            )}
          </Modal>
        </>
      ) : null}
    </>
  );
};
